import { createMemo, type JSX } from 'solid-js';

// Dual-handle range slider. Two native <input name="input" type="range"> elements are
// stacked on top of a styled track; each controls one end of the range.
// The thumbs are the only pointer-interactive part (pointer-events: auto
// on the thumb, none on the track portion of the input) so users can
// grab whichever handle is closest without the top slider eating all
// hits for the bottom one.
//
// Values clamp against each other on input — dragging the min handle
// past the max pushes the max up, and vice versa — so the range can
// never invert.
export default function DualRangeSlider(props: {
  min: number;
  max: number;
  step?: number;
  value: { min: number; max: number };
  onInput: (next: { min: number; max: number }) => void;
  format?: (value: number) => string;
}): JSX.Element {
  const step = () => props.step ?? 1;
  const fmt = (v: number) => (props.format ? props.format(v) : String(v));

  const leftPct = createMemo(
    () => ((props.value.min - props.min) / (props.max - props.min)) * 100,
  );
  const rightPct = createMemo(
    () => ((props.value.max - props.min) / (props.max - props.min)) * 100,
  );

  function handleMin(raw: number) {
    const next = Math.min(raw, props.value.max);
    props.onInput({ min: next, max: props.value.max });
  }
  function handleMax(raw: number) {
    const next = Math.max(raw, props.value.min);
    props.onInput({ min: props.value.min, max: next });
  }

  return (
    <div class="flex flex-col gap-1">
      <div class="flex justify-between text-xs font-display">
        <span>{fmt(props.value.min)}</span>
        <span>{fmt(props.value.max)}</span>
      </div>
      <div class="dual-range relative h-6">
        <div class="absolute inset-x-0 top-1/2 -translate-y-1/2 h-1 rounded-full bg-brand-hex" />
        <div
          class="absolute top-1/2 -translate-y-1/2 h-1 rounded-full bg-brand-moss"
          style={{
            left: `${leftPct()}%`,
            right: `${100 - rightPct()}%`,
          }}
        />
        <input
          type="range" name="range"
          min={props.min}
          max={props.max}
          step={step()}
          value={props.value.min}
          onInput={(e) => handleMin(Number(e.currentTarget.value))}
          class="dual-range__input"
          aria-label="Minimum"
        />
        <input
          type="range" name="range"
          min={props.min}
          max={props.max}
          step={step()}
          value={props.value.max}
          onInput={(e) => handleMax(Number(e.currentTarget.value))}
          class="dual-range__input"
          aria-label="Maximum"
        />
      </div>
    </div>
  );
}
